import type { LensCardData, LensFilterValues } from '~/types/leLens';

import { csvCell } from '~/utils/csvCell';
import { type LensDataReadiness, lensDataReady } from '~/utils/leLensTransport';

import { addDays, lastCompleteDay } from '../ProductPerformance/model';
import { csvMeasure, csvMoney, csvRate } from '../csvValues';

import { clickFields, completeClickCard } from './batches';
import { type Row, number } from './liveModel';
import {
  MEASURES,
  type NumericRow,
  type PlotRow,
  type Report,
  type ReportState,
  type TableRecord,
  metrics,
  normalizeReportParams,
  reportParams,
  selection,
  splitColumns,
  validation,
} from './presentationModel';
import { productMeasureRows } from './productMeasureRows';
import { type ViewKey, activeViewFilters, sheetProblem, sheetRange } from './viewFilters';
import { completeWorksheetCard, sendFields, worksheetFields } from './worksheetModel';

export const viewKeys: Record<ReportState['view'], ViewKey> = {
  'Tables: Key Metrics': 'tables',
  'Charts: Comms Metrics': 'comms',
  'Charts: Financial Metrics': 'financial',
  'Charts: Health Metrics': 'health',
  'Clicks Insights': 'clicks',
  'Clicks Insights (Strategic Regions)': 'regions',
  'Offer Clicks Insights': 'offers',
  'Offer Vendor Insights': 'vendors',
};
export const reportIdentity = (state: ReportState) => reportParams(state).toString();
export const decodeCards = (cards: ReadonlyArray<LensCardData>) =>
  cards.map(completeClickCard).map(completeWorksheetCard);
export function activeDependencies(state: ReportState) {
  if (viewKeys[state.view] === 'clicks') {
    return ['clicks_summary', 'clicks_rows', 'worksheet_rows'];
  }
  return ['tables', 'comms', 'financial', 'health'].includes(viewKeys[state.view])
    ? ['sends_summary', 'sends_rows']
    : ['worksheet_rows'];
}
/** One-row card with the newest send date in each source table; it never blocks the report. */
export const SOURCE_AS_OF_CARD = 'source_as_of';
export const requestedCards = (state: ReportState) => [...activeDependencies(state), SOURCE_AS_OF_CARD];
/** A report card came back with an error inside a successful response, so asking again may help. */
export const reportCardFailed = (state: ReportState, cards: ReadonlyArray<LensCardData>) =>
  cards.some((card) => card.status === 'error' && activeDependencies(state).includes(card.cardId));
const SEND_VIEWS: ReadonlyArray<ViewKey> = ['tables', 'comms', 'financial', 'health'];
/** Newest send date in the source the tab reads: send level for Tables and Charts, click level otherwise. */
export function sourceAsOf(cards: ReadonlyArray<LensCardData>, view: ViewKey): string | null {
  const row = cards.find((card) => card.cardId === SOURCE_AS_OF_CARD && card.status === 'ready')?.rows?.[0];
  const value = row?.[SEND_VIEWS.includes(view) ? 'latest_send_date' : 'latest_click_date'];
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}
const SOURCE_DATE_FORMAT = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});
/** '2026-10-21' reads as '21 Oct 2026'. */
export const formatSourceDate = (iso: string | null) =>
  iso ? SOURCE_DATE_FORMAT.format(Date.parse(iso + 'T00:00:00Z')) : 'Unknown';
export const countryGroupLabel = (value: string) =>
  value === 'Unknown' ? 'Unknown' : value === '' ? '(blank)' : value.toLowerCase();
export function countryGroupMembers(value: string, domain: ReadonlyArray<string> | null): Array<string> {
  return [
    ...new Set(
      selection(value).flatMap((member) => {
        const matched = domain?.filter((raw) => countryGroupLabel(raw) === member);
        return matched?.length ? matched : [member];
      }),
    ),
  ];
}
export function requestComms(
  state: ReportState,
  params: URLSearchParams,
  now: Date,
  countryDomain: ReadonlyArray<string> | null = null,
): LensFilterValues {
  const key = viewKeys[state.view],
    today = addDays(lastCompleteDay(now), 1),
    sheet = sheetRange(key, state, now);
  const cadence = {
    Daily: 'day',
    Weekly: 'week',
    Monthly: 'month',
    Annually: 'year',
    Weekday: 'weekday',
    None: 'none',
  };
  return {
    period: {
      window: {
        from: key === 'clicks' ? state.clickFrom : state.filters.from,
        to: key === 'clicks' ? state.clickTo : state.filters.to,
      },
      comparison: null,
    },
    grain: cadence[state.cadence],
    data_split: key === 'clicks' ? (params.get('click_split') ?? 'product_name') : splitColumns[state.split],
    // Keep cadence operands for charts; Full Period table cells reduce their additive operands, never ratios.
    time_split: 'cadence',
    worksheet_view: ['tables', 'comms', 'financial', 'health'].includes(key) ? '__NONE__' : key,
    report_today: params.get('report_today') ?? today,
    pane_from: params.get('pane_from') ?? lastCompleteDay(now),
    pane_to: params.get('pane_to') ?? today,
    pane_split: params.get('pane_split') ?? 'utm_source',
    trend_from: state.clickFrom,
    trend_to: state.clickTo,
    message_input: params.get('message_input') ?? 'All',
    vendor_mode: params.get('vendor_mode') ?? 'vendor_name',
    // Tableau's Exclude 'Null' set filter on Clicks Insights, In by default.
    vendor_set: params.get('vendor_set') ?? 'In',
    // Offer Vendor's saved TE Cadence and Email Placement come from the filters below, not fixed SQL.
    vendor_restrictions: 'filters',
    sheet_from: sheet.from,
    sheet_to: sheet.to,
    offer_substring: params.has('offer_substring') ? [params.get('offer_substring')] : [],
    ...activeViewFilters(params, key),
    ...(selection(state.filters.channel).length ? { utm_medium: selection(state.filters.channel) } : {}),
    ...(selection(state.filters.market).length
      ? { subscriber_country_group: countryGroupMembers(state.filters.market, countryDomain) }
      : {}),
    ...(selection(state.filters.campaign).length ? { utm_campaign: selection(state.filters.campaign) } : {}),
    ...(key === 'clicks' && state.product !== 'All' ? { product_name: [state.product] } : {}),
  };
}
export function requestProblem(scope: LensFilterValues, params: URLSearchParams): string | null {
  if (
    params.has('click_split') &&
    !['All', 'product_name', 'utm_content', 'utm_position', 'utm_source', 'utm_campaign'].includes(
      params.get('click_split'),
    )
  ) {
    return 'Choose a supported Clicks By Date split.';
  }
  if (
    ['all_metric', 'metric_parameter'].some(
      (key) => params.has(key) && !['clicks', 'gm', 'ttv'].includes(params.get(key)),
    )
  ) {
    return 'Choose a supported worksheet metric.';
  }
  const valid = (value: unknown) =>
    typeof value === 'string' &&
    value.slice(0, 4) !== '0000' &&
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    !Number.isNaN(Date.parse(value)) &&
    new Date(value).toISOString().slice(0, 10) === value;
  if (
    ['pane_from', 'pane_to', 'trend_from', 'trend_to', 'report_today'].some((key) => !valid(scope[key])) ||
    ['sheet_from', 'sheet_to'].some((key) => key in scope && !valid(scope[key]))
  ) {
    return 'An independent worksheet date is invalid.';
  }
  if (
    String(scope.pane_from) > String(scope.pane_to) ||
    String(scope.trend_from) > String(scope.trend_to) ||
    String(scope.sheet_from) > String(scope.sheet_to)
  ) {
    return 'Worksheet From must be on or before To.';
  }
  const tooWide = sheetProblem(scope.worksheet_view as ViewKey, {
    from: String(scope.sheet_from),
    to: String(scope.sheet_to),
  });
  if (tooWide) {
    return tooWide;
  }
  return normalizeReportParams(params).problem;
}
function dimension(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'object' && 'value' in value) {
    return dimension(value.value);
  }
  if (typeof value === 'string') {
    return value;
  }
  throw new Error('Invalid worksheet dimension.');
}
export const dimensionLabel = (value: unknown) => {
  const v = dimension(value);
  return v === null ? 'Unknown' : v === '' ? '(blank)' : v;
};
function numericRow(row: Row, fields: ReadonlyArray<string>, dimensions: ReadonlyArray<string>): NumericRow {
  const output: NumericRow = {};
  for (const field of fields) {
    if (!(field in row)) {
      throw new Error('Missing source operand ' + field);
    }
    if (dimensions.includes(field)) {
      output[field] = dimension(row[field]);
    } else {
      const n = number(row[field]);
      if (row[field] !== null && n === null) {
        throw new Error('Invalid source operand ' + field);
      }
      output[field] = n;
    }
  }
  return output;
}
function rows(cards: ReadonlyArray<LensCardData>, id: string): Array<Row> {
  const c = cards.find((c) => c.cardId === id);
  return c?.rows ?? [];
}
function addOperands(rows: Array<NumericRow>): NumericRow {
  return Object.fromEntries(
    sendFields.slice(2).map((field) => {
      const values = rows.map((row) => row[field]).filter((v): v is number => typeof v === 'number');
      return [field, values.length ? values.reduce((a, b) => a + b, 0) : null];
    }),
  );
}
export type SettledComms = {
  ready: boolean;
  problem: string | null;
  report: Report;
  worksheets: Array<Row>;
  cards: Array<LensCardData>;
  scope: LensFilterValues;
};
export function adaptComms(
  state: ReportState,
  input: LensDataReadiness,
  scope: LensFilterValues,
  constraint: string | null = null,
): SettledComms {
  const cards = decodeCards(input.cards ?? []);
  const report: Report = {
    state,
    identity: reportIdentity(state),
    ready: false,
    sourceAsOf: sourceAsOf(cards, viewKeys[state.view]),
    scope,
    sourceRows: [],
    totals: metrics([], state.filters.mode),
    tableRows: [],
    series: [],
    timeline: [],
    clickDimension:
      {
        product_name: 'Product Name',
        utm_content: 'UTM Content',
        utm_position: 'UTM Position',
        utm_source: 'UTM Source',
        utm_campaign: 'UTM Campaign',
        All: 'All',
      }[String(scope.data_split)] ?? 'Product Name',
    clickRows: [],
    productRows: [],
    products: [],
    clickTimeline: [],
    clickTotal: null,
  };
  const result: SettledComms = { ready: false, problem: null, report, worksheets: [], cards, scope };
  const constraintError = constraint ?? requestProblem(scope, new URLSearchParams());
  if (constraintError) {
    result.problem = constraintError;
    return result;
  }
  const error =
    validation(state.filters) ??
    (viewKeys[state.view] === 'clicks'
      ? validation({ ...state.filters, from: state.clickFrom, to: state.clickTo })
      : null);
  if (error) {
    result.problem = error;
    return result;
  }
  if (!lensDataReady({ ...input, cards }, activeDependencies(state))) {
    result.problem =
      cards.find((card) => card.status === 'error' && activeDependencies(state).includes(card.cardId))?.message ??
      'Waiting for complete live data for this scope.';
    return result;
  }
  try {
    for (const id of activeDependencies(state)) {
      const card = cards.find((card) => card.cardId === id);
      if ((card.status === 'ready' && !card.rows?.length) || (card.status === 'empty' && card.rows?.length)) {
        throw new Error('Incomplete ' + id + ' population.');
      }
    }
    if (activeDependencies(state).includes('sends_rows')) {
      const raw = rows(cards, 'sends_rows').map((row) => numericRow(row, sendFields, ['bucket', 'split']));
      const summary = rows(cards, 'sends_summary');
      if (summary.length > 1) {
        throw new Error('Duplicate overall summary grain.');
      }
      const total = summary.length ? numericRow(summary[0], sendFields.slice(2), []) : {};
      report.sourceRows = raw;
      report.totals = metrics(summary.length ? [total] : [], state.filters.mode);
      const cells = new Set<string>(),
        table = new Map<string, Array<NumericRow>>(),
        dates = new Map<string, Record<string, ReturnType<typeof metrics>>>();
      for (const row of raw) {
        const split = dimensionLabel(row.split),
          bucket = row.bucket === null ? 'Full Period' : String(row.bucket),
          identity = JSON.stringify([split, bucket]);
        if (cells.has(identity)) {
          throw new Error('Duplicate cadence/split population.');
        }
        cells.add(identity);
        const time = state.timeSplit === 'Full Period' ? 'Full Period' : bucket,
          key = JSON.stringify([split, time]);
        table.set(key, [...(table.get(key) ?? []), row]);
        dates.set(bucket, { ...(dates.get(bucket) ?? {}), [split]: metrics([row], state.filters.mode) });
      }
      const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const order = (a: string, b: string) =>
        state.cadence === 'Weekday' ? weekdays.indexOf(a) - weekdays.indexOf(b) : a.localeCompare(b);
      report.tableRows = [...table]
        .map<TableRecord>(([id, group]) => {
          const [split, time]: Array<string> = JSON.parse(id);
          return { id, split, time, values: metrics([addOperands(group)], state.filters.mode) };
        })
        .sort((a, b) => a.split.localeCompare(b.split) || order(b.time, a.time));
      report.series = [...new Set(raw.map((row) => dimensionLabel(row.split)))].sort();
      report.timeline = [...dates].sort(([a], [b]) => order(a, b)).map(([name, values]) => ({ name, values }));
    } else {
      const worksheet = rows(cards, 'worksheet_rows').map((row) =>
        numericRow(row, worksheetFields, worksheetFields.slice(0, 9)),
      );
      result.worksheets = worksheet;
      // Clicks By Date retains its own source bounds and Product Name grouping.
      const clickRows = worksheet
        .filter((row) => row.sheet === 'Clicks By Date')
        .map<
          NumericRow & { date: string; product: string }
        >((row) => ({ ...row, date: String(row.bucket), product: dimensionLabel(row.split) }));
      report.productRows = (
        viewKeys[state.view] === 'clicks'
          ? rows(cards, 'clicks_rows').map((row) => numericRow(row, clickFields, ['bucket', 'split']))
          : []
      ).map((row) => ({ ...row, date: String(row.bucket), product: dimensionLabel(row.split) }));
      report.clickRows = clickRows;
      report.products = [...new Set(clickRows.map((row) => row.product))].sort();
      const rawKeys = new Set<string>(),
        index = new Map<string, Map<string, number | null>>();
      for (const row of clickRows) {
        const rawKey = JSON.stringify([row.bucket, row.split]);
        if (rawKeys.has(rawKey)) {
          throw new Error('Duplicate source date/split grain.');
        }
        rawKeys.add(rawKey);
        const values = index.get(row.date) ?? new Map<string, number | null>(),
          value = number(row[state.filters.mode === 'Unique' ? 'unique_clicks' : 'total_clicks']);
        if (!values.has(row.product)) {
          values.set(row.product, null);
        }
        if (value !== null) {
          values.set(row.product, (values.get(row.product) ?? 0) + value);
        }
        index.set(row.date, values);
      }
      report.clickTimeline = [...index]
        .sort(([a], [b]) => a.localeCompare(b))
        .map<PlotRow>(([date, values]) => ({
          name: date,
          ...Object.fromEntries(report.products.map((product, i) => ['s' + i, values.get(product) ?? null])),
        }));
      if (viewKeys[state.view] === 'clicks') {
        const summary = rows(cards, 'clicks_summary');
        if (summary.length > 1) {
          throw new Error('Duplicate click summary grain.');
        }
        if (summary.length) {
          const total = numericRow(summary[0], clickFields.slice(2), []);
          report.clickTotal = number(total[state.filters.mode === 'Unique' ? 'unique_clicks' : 'total_clicks']);
        }
      }
    }
    result.ready = true;
    report.ready = true;
  } catch (error) {
    result.problem = error instanceof Error ? error.message : 'Incomplete report response.';
  }
  return result;
}
export function commsCsv(report: Report, kind: 'main' | 'products' = 'main'): string {
  if (!report.ready || report.identity !== reportIdentity(report.state)) {
    throw new Error('Customer Comms is not ready for export.');
  }
  const click = viewKeys[report.state.view] === 'clicks';
  if (
    (kind === 'products' && !click) ||
    (kind === 'main' && !['tables', 'comms', 'financial', 'health', 'clicks'].includes(viewKeys[report.state.view]))
  ) {
    throw new Error('Use the recovered worksheet CSV for this view.');
  }
  const metadata = [
    'Scope',
    'Customer Comms Key Report',
    'AUD',
    'sourceAsOf=' + (report.sourceAsOf ?? 'Unknown'),
    'effectiveRequest=' + JSON.stringify(report.scope),
    'sourceCard=' + (kind === 'products' ? 'clicks_rows' : click ? 'worksheet_rows' : 'sends_rows'),
    ...reportParams(report.state).entries(),
  ].map((value) => (Array.isArray(value) ? value.join('=') : value));
  const header =
    kind === 'products'
      ? [
          report.clickDimension === 'Product Name' ? 'Product' : report.clickDimension,
          'Clicks',
          'Items',
          'GM (A$)',
          'TTV (A$)',
          'Items / Clicks',
          'GM / Click (A$)',
        ]
      : click
        ? ['Send Date', ...report.products]
        : ['Data Split', 'Time Split', ...MEASURES.map((m) => m.label)];
  const data: Array<Array<unknown>> =
    kind === 'products'
      ? productMeasureRows(report).map((row) => [
          row.name,
          row.clicks,
          row.items,
          csvMoney(row.gm),
          csvMoney(row.ttv),
          csvRate(row.cr),
          csvMeasure(row.gmPerClick, 'money', 3),
        ])
      : click
        ? report.clickTimeline.map((row) => [row.name, ...report.products.map((_, i) => row['s' + i])])
        : [
            ['Grand Total', '', ...MEASURES.map((m) => csvMeasure(report.totals[m.key], m.kind, m.digits))],
            ...report.tableRows.map((row) => [
              row.split,
              row.time,
              ...MEASURES.map((m) => csvMeasure(row.values[m.key], m.kind, m.digits)),
            ]),
          ];
  return (
    '\uFEFF' +
    [metadata, header, ...data].map((row) => row.map((v) => csvCell(v, 'Unknown')).join(',')).join('\r\n') +
    '\r\n'
  );
}
export function downloadCommsCsv(report: Report, kind: 'main' | 'products' = 'main') {
  const content = commsCsv(report, kind),
    url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' })),
    link = document.createElement('a');
  link.href = url;
  link.download = 'customer-comms-' + kind + '.csv';
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
